import { Header } from 'components/Header'
import { useAppInfo } from 'hooks/useAppInfo'

import { FlexColumn } from '@interco/inter-ui/components/Flex'
import { Spacing } from '@interco/inter-ui/components/Spacing'
import { Button, ListItemControl, Text } from '@interco/inter-toranja'
import type { CheckboxTrailingProps } from '@interco/inter-toranja/dist/components/Molecules/ListItemControl/types'
import {
  TextSize,
  TextType,
  TextWeight,
} from '@interco/inter-toranja/dist/components/Atoms/Text/types'

import { QuestionStepConfig } from './data'

interface QuestionStepProps {
  question: QuestionStepConfig
  selectedIds: string[]
  onSelectOption: (optionId: string) => void
  onBack: () => void
  onContinue: () => void
}

export const QuestionStep = ({
  question,
  selectedIds,
  onSelectOption,
  onBack,
  onContinue,
}: QuestionStepProps) => {
  const { isDarkMode = false } = useAppInfo()

  return (
    <FlexColumn height="100vh" justifyContent="space-between">
      <div className="flex flex-col gap-3 justify-center items-center">
        <Header title={question.headerTitle} onClick={onBack} isDarkMode={isDarkMode} />
        <Spacing mb="xxs" />

        <div className="w-screen flex flex-col justify-center items-center">
          <div className="px-8 text-start">
            <Text
              textType={TextType.Title}
              textSize={TextSize.Large}
              textWeight={TextWeight.Bold}
              as="h1"
            >
              {question.title}
            </Text>
          </div>

          <Spacing mb="xs" />

          {question.options.map((option) => {
            const isSelected = selectedIds.includes(option.id)
            const commonProps = {
              variant: 'inset' as const,
              label: option.label,
              key: option.id,
              state: 'enabled' as const,
              onClick: () => onSelectOption(option.id),
            }

            if (question.type === 'checkbox') {
              return (
                <ListItemControl
                  {...commonProps}
                  trailingVariant="checkbox"
                  trailingProps={
                    {
                      checked: isSelected,
                      onChange: () => onSelectOption(option.id),
                    } as CheckboxTrailingProps
                  }
                />
              )
            }

            return (
              <ListItemControl
                {...commonProps}
                trailingVariant="radio"
                trailingProps={{
                  checked: isSelected,
                  value: option.id,
                  onRadioChange: () => onSelectOption(option.id),
                }}
              />
            )
          })}
        </div>
      </div>

      <FlexColumn className="px-6 pb-6">
        <Button
          hierarchy="primary"
          style={{ width: '100%' }}
          label={question.continueLabel}
          disabled={!selectedIds.length}
          onClick={onContinue}
        />
      </FlexColumn>
    </FlexColumn>
  )
}
